import React from "react";
import { Box, Button, Flex, Heading, HStack, Icon, SimpleGrid, Skeleton, Stack, Text, VStack } from "@chakra-ui/react";
import { FiRefreshCw, FiWifiOff, FiAlertTriangle } from "react-icons/fi";
import { IconType } from "react-icons";
import { alpha, useGovTheme } from "./useGovTheme";
import { GovSurface } from "./GovSurface";
import { FadeIn } from "./motion";
import { GovTip } from "./GovTip";
import { blurClick } from "./blurClick";

/**
 * Loading, error and empty are three different truths (hardening rule 14). These are the only three
 * governance renders — a page never shows "nothing here" while it is still asking, or after it failed.
 */

/** Skeleton cards in the shape of what is coming, instead of a spinner in a void. */
export function GovLoading({ label, cards = 3, columns = { base: 1, md: 2, lg: 3 } }: { label: string; cards?: number; columns?: any }) {
  return (
    <Box role="status" aria-live="polite" aria-label={label}>
      <SimpleGrid columns={columns} spacing={4}>
        {Array.from({ length: cards }).map((_, i) => (
          <GovSurface key={i}>
            <Skeleton h="14px" w="40%" borderRadius="full" mb={4} />
            {/* Plain bars, not SkeletonText: that one reads the screen size through window.matchMedia, which
                jsdom lacks, so every test rendering a loading state crashed on a tenant without a stub. */}
            <Stack spacing={3}>
              {["100%", "92%", "64%"].map((w) => <Skeleton key={w} h="10px" w={w} borderRadius="full" />)}
            </Stack>
          </GovSurface>
        ))}
      </SimpleGrid>
      <Text srOnly>{label}</Text>
    </Box>
  );
}

/** "Failed to fetch" means the server could not be reached — say that in words, keep the raw reason small, offer Retry. */
export function GovError({ title, error, onRetry }: { title: string; error: { message?: string } | Error | string; onRetry?: () => unknown }) {
  const t = useGovTheme();
  const raw = typeof error === "string" ? error : error?.message || "Unknown error";
  const offline = /failed to fetch|networkerror|network request failed|load failed/i.test(raw);
  return (
    <FadeIn>
      <GovSurface role="alert" borderColor={alpha(t.danger, 0.35)} bg={alpha(t.danger, 0.06)}>
        <Stack direction={{ base: "column", sm: "row" }} spacing={4} align={{ sm: "center" }} justify="space-between">
          <HStack spacing={3} align="start">
            <Flex flexShrink={0} w={10} h={10} borderRadius="lg" bg={alpha(t.danger, 0.14)} align="center" justify="center">
              <Icon as={offline ? FiWifiOff : FiAlertTriangle} color={t.danger} />
            </Flex>
            <Box minW={0}>
              <Text fontWeight="semibold" fontSize="md" color={t.text}>{title}</Text>
              <Text fontSize="md" color={t.muted}>{offline ? "The server can’t be reached right now. Check your connection, or try again in a moment." : raw}</Text>
              {offline && <Text fontSize="sm" color={t.faint} mt={1}>Details: {raw}</Text>}
            </Box>
          </HStack>
          {onRetry && (
            <GovTip label="Ask the server again. Nothing is changed.">
              <Button leftIcon={<Icon as={FiRefreshCw} />} variant="outline" size="lg" minH="48px" flexShrink={0} onClick={blurClick(() => { void onRetry(); })}>Try again</Button>
            </GovTip>
          )}
        </Stack>
      </GovSurface>
    </FadeIn>
  );
}

/** A genuinely empty state that teaches: what this is for, and the one thing to do next. */
export function GovEmpty({ icon, title, children, action, points }: { icon: IconType; title: string; children?: React.ReactNode; action?: React.ReactNode; points?: { icon: IconType; text: string }[] }) {
  const t = useGovTheme();
  return (
    <FadeIn>
      <GovSurface textAlign="center" py={{ base: 8, md: 12 }} px={{ base: 5, md: 10 }}>
        <VStack spacing={4} maxW="560px" mx="auto">
          <Flex w={16} h={16} borderRadius="2xl" bgImage={t.accentGradient} align="center" justify="center" boxShadow="0 12px 32px rgba(56,158,255,0.35)">
            <Icon as={icon} boxSize={7} color="white" />
          </Flex>
          <Heading size="md" letterSpacing="-0.02em" color={t.text}>{title}</Heading>
          {children && <Text fontSize="md" color={t.muted}>{children}</Text>}
          {points && (
            <SimpleGrid columns={{ base: 1, sm: points.length > 2 ? 3 : points.length }} spacing={3} w="100%" pt={1}>
              {points.map((p) => (
                <VStack key={p.text} spacing={2} p={3} borderRadius="xl" bg={t.raised}>
                  <Icon as={p.icon} color={t.primary} />
                  <Text fontSize="sm" color={t.muted}>{p.text}</Text>
                </VStack>
              ))}
            </SimpleGrid>
          )}
          {action}
        </VStack>
      </GovSurface>
    </FadeIn>
  );
}
